import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import {
  useChannel,
  useChannelOverlays,
  useDeleteOverlay,
  useTemplates,
  useUpdateOverlay,
} from '../lib/queries'
import { toast } from '../lib/toast'
import type { Overlay } from '../types'
import AppShell from '../components/AppShell'
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import { SkeletonCard } from '../components/ui/Skeleton'
import { useQueryClient } from '@tanstack/react-query'

function CopyBtn({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          /* clipboard API yok — sessizce yut */
        }
      }}
      title="URL'yi kopyala"
      className="shrink-0 p-1 rounded text-[var(--color-text-subtle)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface)]"
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-success)]">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  )
}

export default function ChannelPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: channel, isLoading: chLoading } = useChannel(id)
  const { data: overlays = [], isLoading: olLoading } = useChannelOverlays(id)
  const { data: templates = [] } = useTemplates()
  const updateOverlay = useUpdateOverlay(id || '')
  const deleteOverlay = useDeleteOverlay(id || '')

  const [name, setName] = useState('')
  const [tmpl, setTmpl] = useState<string>('voting/election')
  const [confirmDelete, setConfirmDelete] = useState<Overlay | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null)

  if (chLoading || olLoading) {
    return (
      <AppShell activeChannelId={id || null}>
        <div className="max-w-5xl mx-auto px-6 py-8 space-y-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </AppShell>
    )
  }

  if (!channel) {
    return (
      <AppShell activeChannelId={id || null}>
        <div className="max-w-5xl mx-auto px-6 py-8 text-sm text-[var(--color-text-muted)]">
          Kanal bulunamadı.
        </div>
      </AppShell>
    )
  }

  const activeOverlays = overlays.filter((o) => o.is_active).length

  async function createOverlay(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !id) return
    const [type, variant] = tmpl.split('/')
    try {
      await api<Overlay>(`/api/channels/${id}/overlays`, {
        method: 'POST',
        body: JSON.stringify({ name, type, variant }),
      })
      setName('')
      qc.invalidateQueries({ queryKey: ['channels', id, 'overlays'] })
      toast.success('Overlay oluşturuldu')
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <AppShell activeChannelId={id || null}>
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <button
              onClick={() => navigate('/dashboard?stay=1')}
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-2"
            >
              ← Tüm kanallar
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold">{channel.slug}</h1>
              <Badge tone={channel.is_owner ? 'accent' : 'neutral'}>{channel.role}</Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(`/channels/${channel.id}/links`)}>
              Linkler
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/channels/${channel.id}/settings`)}>
              ⚙ Ayarlar
            </Button>
          </div>
        </div>

        {/* Quick info */}
        <Section title="Genel bakış">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Overlay" value={overlays.length} />
            <Stat label="Aktif" value={activeOverlays} />
            <Stat
              label="Bot"
              value={channel.bot_enabled ? 'Aktif' : 'Pasif'}
              tone={channel.bot_enabled ? 'success' : 'neutral'}
            />
            <Stat
              label="OAuth"
              value={channel.oauth_status === 'ok' ? 'OK' : channel.oauth_status}
              tone={channel.oauth_status === 'ok' ? 'success' : 'warning'}
            />
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <span>Kick:</span>
            <a
              href={`https://kick.com/${channel.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-accent-hover)] hover:underline font-mono"
            >
              kick.com/{channel.slug}
            </a>
            <CopyBtn url={`https://kick.com/${channel.slug}`} />
          </div>
        </Section>

        {/* Overlays */}
        <Section title="Overlay'ler" description="OBS browser source URL'leri">
          <form onSubmit={createOverlay} className="flex gap-2 mb-4">
            <Input
              placeholder="Yeni overlay adı"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <select
              value={tmpl}
              onChange={(e) => setTmpl(e.target.value)}
              className="h-9 px-3 rounded-md text-sm bg-[var(--color-elevated)] border border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent)] focus:outline-none"
            >
              {templates.map((t) => (
                <option key={`${t.type}/${t.variant}`} value={`${t.type}/${t.variant}`}>
                  {t.label}
                </option>
              ))}
            </select>
            <Button type="submit" variant="primary">
              Oluştur
            </Button>
          </form>

          <ul className="space-y-1.5">
            {overlays.length === 0 && (
              <li className="text-xs text-[var(--color-text-subtle)]">Henüz overlay yok.</li>
            )}
            {overlays.map((o) => {
              const url = `https://kick.pxlab.dev/o/${o.token}`
              const isRenaming = renaming?.id === o.id
              const displayName = o.title || o.name
              return (
                <li
                  key={o.id}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border bg-[var(--color-elevated)] hover:border-[var(--color-border-strong)] ${
                    o.is_active ? 'border-[var(--color-border)]' : 'border-[var(--color-border)] opacity-60'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault()
                          if (!renaming) return
                          updateOverlay.mutate(
                            { id: o.id, patch: { title: renaming.value.trim() || '' } },
                            {
                              onSuccess: () => {
                                toast.success('Başlık güncellendi')
                                setRenaming(null)
                              },
                              onError: (err) => toast.error((err as Error).message),
                            },
                          )
                        }}
                        className="flex items-center gap-2"
                      >
                        <Input
                          value={renaming.value}
                          onChange={(e) => setRenaming({ id: o.id, value: e.target.value })}
                          autoFocus
                          placeholder={o.name}
                          className="text-sm h-7"
                        />
                        <Button type="submit" variant="primary" size="sm">
                          Kaydet
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRenaming(null)}>
                          İptal
                        </Button>
                      </form>
                    ) : (
                      <button
                        onClick={() => channel.is_owner && setRenaming({ id: o.id, value: o.title || '' })}
                        className="font-medium text-sm truncate text-left hover:text-[var(--color-accent-hover)] cursor-text"
                        title={channel.is_owner ? 'Başlığı düzenle' : ''}
                      >
                        {displayName}
                      </button>
                    )}
                    <div className="text-xs text-[var(--color-text-muted)] mt-0.5 flex items-center gap-1.5">
                      <span className="font-mono shrink-0">
                        {o.type}/{o.variant}
                      </span>
                      <span className="text-[var(--color-text-subtle)]">·</span>
                      <code className="truncate">{url}</code>
                      <CopyBtn url={url} />
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Yeni sekmede aç"
                        className="shrink-0 p-1 rounded text-[var(--color-text-subtle)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface)]"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {channel.is_owner && (
                      <label
                        className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer mr-1"
                        title={o.is_active ? 'Pasifleştir' : 'Aktifleştir'}
                      >
                        <input
                          type="checkbox"
                          checked={o.is_active}
                          onChange={(e) =>
                            updateOverlay.mutate(
                              { id: o.id, patch: { is_active: e.target.checked } },
                              {
                                onSuccess: () =>
                                  toast.success(e.target.checked ? 'Aktifleştirildi' : 'Pasifleştirildi'),
                                onError: (err) => toast.error((err as Error).message),
                              },
                            )
                          }
                          className="accent-[var(--color-accent)]"
                        />
                        <span>{o.is_active ? 'Aktif' : 'Pasif'}</span>
                      </label>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/overlay/${o.id}/edit`)}
                    >
                      Düzenle
                    </Button>
                    {channel.is_owner && (
                      <Button variant="danger" size="sm" onClick={() => setConfirmDelete(o)}>
                        Sil
                      </Button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </Section>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return
          try {
            await deleteOverlay.mutateAsync(confirmDelete.id)
            toast.success('Overlay silindi')
          } catch (e) {
            toast.error((e as Error).message)
          }
        }}
        title="Overlay sil?"
        description={
          confirmDelete
            ? `${confirmDelete.title || confirmDelete.name} silinir. OBS browser source URL'i kırılır.`
            : ''
        }
        confirmLabel="Sil"
        destructive
      />
    </AppShell>
  )
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string | number
  tone?: 'neutral' | 'success' | 'warning'
}) {
  const colors: Record<string, string> = {
    neutral: 'text-[var(--color-text)]',
    success: 'text-[var(--color-success)]',
    warning: 'text-[var(--color-warning)]',
  }
  return (
    <div>
      <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className={`text-lg font-semibold ${colors[tone]}`}>{value}</div>
    </div>
  )
}
